import {
  WORKFLOW_STEPS,
  STORNO_STATUS,
  INTERN_STATUS,
  workflowStepIndex,
} from "@/lib/workflow";
import type { VerlaufEntry } from "@/api/bookingDetail";

interface WorkflowProgressProps {
  status: string;
  verlauf?: VerlaufEntry[];
  marginBottom?: number | string;
}

function tooltipForStep(stepName: string, verlauf: VerlaufEntry[]): string | undefined {
  const match = [...verlauf]
    .reverse()
    .find(
      (e) =>
        e.aktion === "Workflow" &&
        e.details != null &&
        e.details.includes(stepName),
    );
  if (!match) return undefined;
  const datumStr = match.zeitpunkt
    ? new Date(match.zeitpunkt).toLocaleString("de-DE", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : null;
  const parts = [stepName];
  if (datumStr) parts.push(`am ${datumStr}`);
  if (match.akteur) parts.push(`· ${match.akteur}`);
  return parts.join(" ");
}
// MarginBottom für Abstand zum nächsten Element (z.B. Tabs oder Buttons). Default 16px, kann aber auch z.B. "0" sein, wenn kein Abstand gewünscht ist.
export function WorkflowProgress({ status, verlauf = [], marginBottom = 16 }: WorkflowProgressProps) {
  const isStorno = status === STORNO_STATUS;
  const isIntern = status === INTERN_STATUS;
  const isSonder = isStorno || isIntern;

  if (isStorno) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 2px", marginBottom }}>
        <span style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--status-cancel)", flexShrink: 0 }} />
        <span style={{ fontSize: 12, fontWeight: 600, color: "var(--status-cancel)" }}>Storniert</span>
        <span style={{ fontSize: 11, color: "var(--fg-muted)" }}>· Diese Buchung wurde storniert</span>
      </div>
    );
  }

  if (
    !isSonder &&
    !WORKFLOW_STEPS.includes(status as typeof WORKFLOW_STEPS[number])
  ) {
    return null;
  }

  // Bei Intern: normaler Workflow + extra Schritt am Ende
  const steps: string[] = isIntern
    ? [...WORKFLOW_STEPS, INTERN_STATUS]
    : [...WORKFLOW_STEPS];

  const idx = isIntern ? steps.length - 1 : workflowStepIndex(status);

  return (
    <div style={{ padding: "4px 2px 2px", marginBottom }}>
      {/* Segmentierte Linie */}
      <div style={{ display: "flex", alignItems: "center", gap: 3 }}>
        {steps.map((step, i) => {
          const done = i < idx;
          const current = i === idx;
          const bg =
            current || done
              ? "var(--brand-700)"
              : "color-mix(in oklab, var(--border) 70%, transparent)";
          const tip =
            i < idx && verlauf.length > 0
              ? tooltipForStep(step, verlauf)
              : undefined;
          return (
            <div
              key={step}
              title={tip}
              style={{
                flex: 1,
                height: 3,
                borderRadius: 2,
                background: bg,
                cursor: tip ? "help" : "default",
              }}
            />
          );
        })}
      </div>

      {/* Labels */}
      <div style={{ display: "flex", alignItems: "flex-start", gap: 3, marginTop: 8 }}>
        {steps.map((step, i) => {
          const done = i < idx;
          const current = i === idx;
          const color = current
            ? "var(--fg)"
            : done
            ? "var(--fg-muted)"
            : "var(--fg-subtle)";
          return (
            <div
              key={step}
              style={{
                flex: 1,
                minWidth: 0,
                fontSize: 10.5,
                lineHeight: 1.25,
                fontFamily: "var(--font-body)",
                color,
                fontWeight: current ? 600 : 500,
                letterSpacing: current ? 0 : ".01em",
                wordBreak: "break-word",
              }}
            >
              {step}
            </div>
          );
        })}
      </div>
    </div>
  );
}
